import {execute} from './lang/scripting.js';
import {getCoordinates} from './getCoordinates.js';
import {
    NONE,
    BOUNCE_1,
    BOUNCE_2,
    SWAP,
    SPIRAL,
    swap,
    life,
    expand,
    EXPAND,
    LIFE,
    ROTATE,
    SCRIPT,
    rotate} from './lang/operations.js';
import {
    bounce} from './lang/bounce.js';
import {saveParameterToJson, loadParameterFromJson} from 'parameters/parameterJsonFunctions.js';
import Sequenced from './Sequenced.js';
import Genetic from './Genetic.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import ParameterContext from 'effects/ParameterContext.js';
import {uuid} from 'uuidv4';
import Scriptable from './lang/Scriptable.js';
import VariableEffect from "effects/midi/scripting/VariableEffect.js";
import {setEffectParameterValues} from 'actions/index.js';
import {compose} from "utils/compose.js";
import AbstractEffect from 'effects/AbstractEffect.js';

export default class PlaneGroup extends compose(
    Scriptable,
    VariableEffect,
    Genetic,
    Sequenced)
(AbstractEffect) {
    constructor(parameters=[], algorithm=NONE, context, track, effect) {
        super("Plane Group", track);
        this.initialBulkValues = {};
        this.effect = effect;
        this.id = uuid();
        this._parameters = parameters;
        this.track = track;
        this.context = context || new ParameterContext().forTrack(track);
        this.parameterIds = [];
        this.counter = 0;
        this.parameters = [];

        this.algorithmParameter = new AbstractParameter(
            'width', this, () => [],
            {
                defaultValue: algorithm,
                min: 0,
                max: 1
            });

        this.widthParameter = new AbstractParameter(
            'width', this, () => [],
            {
                defaultValue: 12,
                min: 4,
                max: 30
            });

        this.speedParameter = new AbstractParameter(
            'speed', this, () => [],
            {
                defaultValue: 1,
                min: 0.1,
                max: 8
            });

        this.registerParameter(this.algorithmParameter);
        this.registerParameter(this.widthParameter);
        this.registerParameter(this.speedParameter);

        this.bounce = bounce(45);
        this.life = life();
        this.swap = swap();
        this.rotate = rotate(3);

        this.history = [];
        this.updateIdToParameter();

        this.track.sequencerEngine.dispatch(
            setEffectParameterValues(this.initialBulkValues));
    }

    registerParameter(parameter) {
        if (this.context) {
            parameter.setContext(this.context);
        }
        this.initialBulkValues = {
            ... this.initialBulkValues,
            ...parameter.setStore(this.track.store)
        };
        this.parameters.push(parameter);
    }

    ungroupParameter(param) {
        let i = this._parameters.indexOf(param);
        this._parameters.splice(i, 1);
        let group = param.hasGroup;
        param.hasGroup = undefined;

        for (let variation of this.variations) {
            if (Object.keys(variation).includes(param.id)) {
                // then we need to get rid of this parameter from this variation
                delete variation[param.id];
            }
        }
    }

    updateIdToParameter() {
        this.idToParameter = {};
        this._parameters.forEach(p => this.idToParameter[p.id] = p);
    }

    set width(x) {
        this.widthParameter.set(x);
    }

    set speed(x) {
        this.speedParameter.set(x);
    }

    get width() {
        return this.widthParameter.get();
    }

    get speed() {
        return this.speedParameter.get();
    }

    get algorithm() {
        return this.algorithmParameter.get();
    }

    set algorithm(x) {
        this.algorithmParameter.set(x);
    }

    // on each trigger it will algorithm the group a certain way
    getAllParameterCoords() {
        let coords = {};
        getCoordinates(this._parameters)
            .forEach((c, i) => coords[this._parameters[i].id] = c);
        return coords;
    }
    
    onTrigger(step, time, rampTime, ratio) {
        let sequenced = this.sequenceStep(step, time, rampTime);
        if (sequenced) {
            return sequenced;
        }

        let counter = this.counter++;

        let parameters = this._parameters;
        let parameterCoords = {};
        parameters.forEach(
            p => {
                parameterCoords[p.id]= p.getCoordinates();
            });

        // keep track of parameter coords as they come
        this.history.push(parameterCoords);

        let args = [
            this.speed,
            this.width,
            step,
            this.history,
            ratio,
            this.effect];

        let transformed = {};
        if (parameters.length === 0) {
            return {};
        } else if (this.algorithm === LIFE) {
            transformed = this.life(
                parameterCoords, ...args);
        } else if (this.algorithm === SWAP) {
            transformed = this.swap(parameterCoords, ... args);
        } else if (this.algorithm === BOUNCE_1) {
            let bounced = this.bounce(parameterCoords, ...args);
            transformed = this.life(bounced, this.speed / 8.0, this.width, step, this.history, ratio, this.effect);
        } else if (this.algorithm === BOUNCE_2) {
            let bounced = this.bounce(parameterCoords, this.speed, this.width, step, this.history, ratio, this.effect);
            transformed = this.swap(bounced, ...args);
        } else if (this.algorithm === ROTATE) {
            transformed = this.rotate(parameterCoords, ...args);
        } else if (this.algorithm === SPIRAL) {
            let expanded = this.life(parameterCoords, ...args);
            transformed = this.rotate(expanded, ... args);
        } else if (this.algorithm === SCRIPT){
            if (this.script) {
                transformed = execute(this.script, parameterCoords, ...args);
            }
        }

        if (Object.keys(transformed).length === 0) {
            return {};
        }
        
        // We then actually apply the transformation
        let bulk = {};
        for (let _id in transformed) {
            try {
                let density = transformed[_id].density || 1;
                let parameter = this.idToParameter[_id];
                if (parameter) {
                    let {id, value} = parameter.setCoordinates(
                        transformed[_id].x, transformed[_id].y, time, density * rampTime / 1000.0, true);
                    bulk[id] = value;
                }
            } catch (err) {
                console.log("ERROR = ", err);
            }
        }
        return bulk;
    }

    fromJson(json, isCopy=false) {
        if (json.code) {
            this.code = json.code;
        }
        this.restartScript();
        super.fromJson(json, isCopy);
        if (json.id) {
            this.id = json.id;
        }
        this.algorithm = json.algorithm;
        loadParameterFromJson(
            json, 'algorithmParameter', this.algorithmParameter);
        this.speed = json.speed;
        this.width = json.width;
        this.parameterIds = json.parameters;

        if (json.stepToState) {
            this.stepToState = json.stepToState;
        }

        if (json.variations) {
            this.variations = json.variations;
            this.generation = json.generation;
        }
    }

    loadParameters(parameters, newMappings) {
        let parameterIds = this.parameterIds.map(
            id => newMappings[id] ? newMappings[id].id : id);
        for (let parameter of parameters) {
            if (parameterIds.includes(parameter.id) &&
                !this._parameters.includes(parameter)) {
                this._parameters.push(parameter);
                parameter.hasGroup = this;
            }
        }
    }

    getJson() {
        let json = {
            ... super.getJson(),
            id: this.id,
            parameters: this._parameters.map(x => x.id),
            algorithm: this.algorithm,
            width: this.width,
            code: this.code,
            speed: this.speed,
            stepToState: this.stepToState,
            variations: this.variations,
            generation: this.generation
        };
        json = saveParameterToJson(
            json, 'algorithmParameter', this.algorithmParameter);
        return json;
    }

    updateParameter(parameter) {
        if (this.selectedVariation !== undefined) {
            let v = this.variations[this.selectedVariation];
            if (v) {
                v[parameter.id] = parameter.getCoordinates();
            }
        }

        let coordinates = parameter.getCoordinates();
        if (this.stepSelected !== undefined) {
            let state = this.stepToState[this.stepSelected] || {};
            state[parameter.id] = coordinates; 
            this.stepToState[this.stepSelected] = {... state};
        }

        if (this.history && this.history[0]) {
            // change the history so that resets take into account this new position
            // making the whole thing more inteactive
            this.history[0][parameter.id] = coordinates;
        }
    }

    stop() {
        this.restartScript();
        this.updateIdToParameter();
        this.history = [];
    }

    replaceParameter(parameter) {
        for (let i in this._parameters) {
            if (this._parameters[i].id === parameter.id) {
                this._parameters[i] = parameter;
                this.updateIdToParameter();
                parameter.hasGroup = this;
                return;
            }
        }
    }
 
}
